import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { TenantMetadata, getPortalType, TenantPortalType } from '@/features/tenant/types';

/**
 * Tenant Store - Manages current tenant and tenant switching
 * Session 2B: Updated to use TenantMetadata from backend
 */

interface TenantState {
  currentTenant: TenantMetadata | null;
  availableTenants: TenantMetadata[];
  isLoading: boolean;
  error: string | null;
  retryCount: number;
  lastAttemptTime: number | null;

  // Computed properties
  currentPortalType: TenantPortalType | null;

  // Actions
  setCurrentTenant: (tenant: TenantMetadata) => void;
  setAvailableTenants: (tenants: TenantMetadata[]) => void;
  switchTenant: (tenantId: string) => Promise<void>;
  loadTenants: () => Promise<void>;
  clearTenant: () => void;
  resetRetry: () => void;
}

// Constants for retry logic
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_BACKOFF_MS = 2000; // 2 seconds base delay

const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      currentTenant: null,
      availableTenants: [],
      isLoading: false,
      error: null,
      retryCount: 0,
      lastAttemptTime: null,

      // Portal type derived from currentTenant. Recomputed on every setter
      // that changes currentTenant. A plain field is used (not a getter) so
      // Zustand preserves it across setState calls.
      currentPortalType: null,

      setCurrentTenant: (tenant: TenantMetadata) => {
        set({
          currentTenant: tenant,
          currentPortalType: getPortalType(tenant.tenantType),
        });
      },

      setAvailableTenants: (tenants: TenantMetadata[]) => {
        set({ availableTenants: tenants });
      },

      switchTenant: async (tenantId: string) => {
        set({ isLoading: true, error: null });
        try {
          // Use tenant metadata API endpoint
          const response = await fetch(`/api/v1/tenants/${tenantId}/switch`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });

          if (!response.ok) {
            throw new Error('Failed to switch tenant');
          }

          const tenant: TenantMetadata = await response.json();
          set({
            currentTenant: tenant,
            currentPortalType: getPortalType(tenant.tenantType),
            isLoading: false,
          });

          // Reload page to refresh all data with new tenant context
          window.location.reload();
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to switch tenant',
            isLoading: false,
          });
        }
      },

      loadTenants: async () => {
        const state = get();

        // Prevent retry if max attempts reached
        if (state.retryCount >= MAX_RETRY_ATTEMPTS) {
          console.warn(`Max retry attempts (${MAX_RETRY_ATTEMPTS}) reached for loading tenants. Aborting.`);
          set({
            error: `Failed to load tenant after ${MAX_RETRY_ATTEMPTS} attempts. Please check backend connection.`,
            isLoading: false,
          });
          return;
        }

        // Implement exponential backoff
        const now = Date.now();
        if (state.lastAttemptTime) {
          const timeSinceLastAttempt = now - state.lastAttemptTime;
          const requiredDelay = RETRY_BACKOFF_MS * Math.pow(2, state.retryCount);

          if (timeSinceLastAttempt < requiredDelay) {
            console.log(`Backoff in effect. Wait ${requiredDelay - timeSinceLastAttempt}ms before retry.`);
            return;
          }
        }

        set({
          isLoading: true,
          error: null,
          lastAttemptTime: now,
        });

        try {
          // Use tenant metadata API endpoint - get current tenant
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

          const response = await fetch('/api/v1/tenants/current', {
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`Failed to load tenant: ${response.status} ${response.statusText}`);
          }

          const tenant: TenantMetadata = await response.json();

          // Success - reset retry counter
          set({
            currentTenant: tenant,
            currentPortalType: getPortalType(tenant.tenantType),
            availableTenants: [tenant],
            isLoading: false,
            retryCount: 0,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to load tenant';
          const isAbortError = error instanceof Error && error.name === 'AbortError';

          console.error(`Tenant load attempt ${state.retryCount + 1}/${MAX_RETRY_ATTEMPTS} failed:`, errorMessage);

          set({
            error: isAbortError ? 'Request timeout - backend may be unavailable' : errorMessage,
            isLoading: false,
            retryCount: state.retryCount + 1,
          });
        }
      },

      clearTenant: () => {
        set({
          currentTenant: null,
          currentPortalType: null,
          availableTenants: [],
          error: null,
          retryCount: 0,
          lastAttemptTime: null,
        });
      },

      resetRetry: () => {
        set({
          retryCount: 0,
          lastAttemptTime: null,
          error: null,
        });
      },
    }),
    {
      name: 'tenant-storage',
      partialize: (state) => ({
        currentTenant: state.currentTenant,
      }),
    }
  )
);

export default useTenantStore;